import type { FunctionComponent, MouseEvent } from "react";
import { useEffect, useState } from "react";
import Add from "@mui/icons-material/Add";
import ModeEdit from "@mui/icons-material/ModeEdit";
import Delete from "@mui/icons-material/Delete";
import {
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Grid,
  Popover,
  Typography,
  TextField,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useSelector } from "../../../hooks/redux";
import { useHttpRequest } from "../../../hooks/rest";
import CustomTooltip from "../../../components/Tooltip";
import {
  parseMultiboxName,
  sortStringsLikeHuman,
} from "../../../utilities/utilityFunctions";
import { useSnackbar } from "../../../hooks/snackbar";

const useStyles = makeStyles({
  divider: {
    width: "100%",
  },
  grid: {
    padding: "5px",
    maxWidth: "500px",
  },
  popover: {
    overflowY: "hidden",
    padding: "5px",
  },
});

/*interface submissionData {
  productionName: string;
  controlPanels: string[];
}*/

const EditProductionButton: FunctionComponent = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const isOpen = Boolean(anchorEl);

  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();

  const { accountGuid, productionGuid } = useSelector(
    (state) => state.queue.showInfo
  );

  interface Endpoint {
    inCall: boolean;
    // add other properties if needed
  }

  const currentEndpoint = useSelector(
    (state: any) => state.queue.currentScreener
  );

  const currentProduction = useSelector(
    (state: any) => state.queue.currentShow
  );

  const endpoints = useSelector(
    (state: any) => state.queue.screeners
  ) as Record<string, Endpoint>;

  const anyEndpointsAreInCall = Object.values(endpoints).some(
    (endpoint) => endpoint.inCall
  );

  const [updateProductionRequest, updateProductionRequestState] =
    useHttpRequest(`admin_proxy/productions/${productionGuid}/controlPanels`, {
      method: "PUT",
      body: {
        accountGuid,
        productionGuid,
      },
      onError: (e: any) => {
        if (!e.message?.includes?.("signal is aborted without reason")) {
          enqueueSnackbar(
            `Failed to update Production "${currentProduction}" with error: "${
              e.message ?? "Unknown Error"
            }"`,
            { hideIconVariant: true, persist: true, variant: "error" }
          );
        }
        handleClose();
      },
      onSuccess: () => {
        enqueueSnackbar(
          `Production "${currentProduction}" was successfully updated. Users may need to re-select it from the dropdown.`,
          { hideIconVariant: true, persist: true, variant: "success" }
        );
        handleClose();
      },
    });

  const isDisabled =
    updateProductionRequestState.loading ||
    currentEndpoint ||
    anyEndpointsAreInCall;

  const endpointNames = Object.keys(endpoints).sort((a, b) =>
    sortStringsLikeHuman(parseMultiboxName(a), parseMultiboxName(b))
  );

  const [editedEndpointNames, setEditedEndpointNames] = useState<string[]>([]);
  const [changesWereMade, setChangesWereMade] = useState<boolean>(false);

  // Sync editedEndpointNames with endpointNames only when the popover is opened (not when endpointNames changes)
  useEffect(() => {
    if (isOpen) {
      setEditedEndpointNames(endpointNames);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const [addingOnAir, setAddingOnAir] = useState(false);
  const [addingPrescreen, setAddingPrescreen] = useState(false);

  function addEndpoint(type: string) {
    let name = "Control Panel";
    if (type === "prescreen") {
      setAddingPrescreen(true);
      let countP = editedEndpointNames.filter((n) =>
        n.startsWith("Prescreen")
      ).length;
      // Find the next available unique name
      do {
        name = `Prescreen ${++countP}`;
      } while (editedEndpointNames.includes(name));
      setEditedEndpointNames((prev) => [...prev, name]);
      setAddingPrescreen(false);
    } else {
      setAddingOnAir(true);
      let countA = editedEndpointNames.filter((n) =>
        n.startsWith("Endpoint")
      ).length;
      // Find the next available unique name
      do {
        name = `Endpoint ${++countA}`;
      } while (editedEndpointNames.includes(name));
      setEditedEndpointNames((prev) => [...prev, name]);
      setAddingOnAir(false);
    }
    setChangesWereMade(true);
  }

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setEditedEndpointNames(endpointNames);
    setChangesWereMade(false);
    setAnchorEl(null);
  }

  async function handleSaveClick() {
    setWarningDialogOpen(true);
  }

  function handleCancelClick() {
    handleClose();
  }

  function handleRemoveControlPanelClick(name: string) {
    setEditedEndpointNames((prev) =>
      prev.filter((endpointName) => endpointName !== name)
    );
    setChangesWereMade(true);
  }

  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  return (
    <>
      <CustomTooltip
        title={
          isDisabled
            ? currentEndpoint
              ? "You must go back to the main screen before editing the current Production"
              : "Please end all calls before editing the current Production"
            : "Click to edit the current Production"
        }
      >
        <IconButton
          color={Boolean(anchorEl) ? "success" : "info"}
          disabled={isDisabled}
          size="medium"
          onClick={handleClick}
        >
          <ModeEdit fontSize="medium" />
        </IconButton>
      </CustomTooltip>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        classes={{
          paper: classes.popover,
        }}
        open={isOpen}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        onClose={handleClose}
      >
        <Grid className={classes.grid} container spacing={1}>
          <Grid
            alignItems="center"
            container
            item
            justifyContent="space-between"
          >
            <Typography>
              <b>Edit Control Panels for {currentProduction}</b>
            </Typography>
          </Grid>
          <Divider className={classes.divider} component="div" />

          <Grid
            alignItems="center"
            container
            item
            justifyContent="space-between"
            spacing={1}
          >
            <Grid item>
              <Typography>Add Control Panels</Typography>
            </Grid>
            <Grid item>
              <Button
                disabled={
                  addingPrescreen || updateProductionRequestState.loading
                }
                size="small"
                startIcon={<Add />}
                onClick={() => addEndpoint("prescreen")}
              >
                Prescreen
              </Button>
            </Grid>
            <Grid item>
              <Button
                disabled={addingOnAir || updateProductionRequestState.loading}
                size="small"
                startIcon={<Add />}
                onClick={() => addEndpoint("onAir")}
              >
                On-Air
              </Button>
            </Grid>
          </Grid>

          <Grid container direction="column" item spacing={1}>
            {editedEndpointNames.map((name, idx) => (
              <Grid
                alignItems="center"
                container
                direction="row"
                item
                key={idx}
              >
                <Grid item xs={11}>
                  <TextField
                    disabled={updateProductionRequestState.loading}
                    fullWidth
                    id={`edit-control-panel-${idx}`}
                    inputProps={{ maxLength: 100 }}
                    size="small"
                    value={name}
                    variant="outlined"
                    onChange={(e) => {
                      const newName = e.target.value;
                      if (newName.trim() !== "") {
                        setEditedEndpointNames((prev) =>
                          prev.map((n, i) => (i === idx ? newName : n))
                        );
                        setChangesWereMade(true);
                      }
                    }}
                  />
                </Grid>
                <Grid alignItems="center" item xs={1}>
                  <IconButton
                    color="error"
                    disabled={updateProductionRequestState.loading}
                    size="small"
                    onClick={() => handleRemoveControlPanelClick(name)}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Grid>

          <Grid container item justifyContent="space-between" spacing={1}>
            <Grid item>
              <Button
                color="error"
                disabled={updateProductionRequestState.loading}
                variant="contained"
                onClick={handleCancelClick}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <CustomTooltip
                title={
                  isDisabled
                    ? updateProductionRequestState.loading
                      ? "Please wait..."
                      : currentEndpoint
                      ? "You must go back to the main screen before editing the current Production"
                      : "Please end all calls before editing the current Production"
                    : !changesWereMade
                    ? "Make changes to the current Production in order to save them"
                    : "Click to save changes to the current Production"
                }
              >
                <Button
                  color="success"
                  disabled={isDisabled ? true : !changesWereMade}
                  variant="contained"
                  onClick={handleSaveClick}
                >
                  Save
                </Button>
              </CustomTooltip>
            </Grid>
          </Grid>
        </Grid>
      </Popover>

      <Dialog
        open={warningDialogOpen}
        onClose={() => setWarningDialogOpen(false)}
      >
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Saving these changes will cause the Production named "
            {currentProduction}" to be inaccessible to ALL USERS for
            approximately 30 seconds while changes are applied.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={updateProductionRequestState.loading}
            onClick={() => setWarningDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            autoFocus
            disabled={updateProductionRequestState.loading}
            variant="contained"
            onClick={async () => {
              setWarningDialogOpen(false);

              const controlPanels = editedEndpointNames.sort((a, b) =>
                sortStringsLikeHuman(parseMultiboxName(a), parseMultiboxName(b))
              );

              await updateProductionRequest({
                body: { controlPanels },
              });
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditProductionButton;
